import { UseFormReturn } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'

import { cn } from '@/lib/utils'
import { setActiveProposal } from '@/lib/assistantAudit'
import { AssistantModel } from '@/http/models/assistant/Assistant'
import { AssistantProposal, ProductProposalFields } from '@/types/assistant'
import { ProductModel } from '@/types'

import { useAssistant } from './AssistantProvider'
import { scrollReviewIntoView, useReviewStamp } from './useReviewStamp'

/** Field names the assistant changed on the current form, for a highlight. Empty outside a review. */
export const AssistantReviewContext = createContext<Set<string>>(new Set())

/** Class for a field wrapper: tinted while it holds an assistant-prepared value. */
export const useChangedClass = () => {
  const changed = useContext(AssistantReviewContext)
  return (field: string, base?: string) => cn(base, changed.has(field) && '-m-2 rounded-lg bg-violet-lighter p-2 ring-1 ring-violet-light')
}

type ProductProposal = Extract<AssistantProposal, { kind: 'product_create' | 'product_update' }>

/**
 * Applies a product proposal (?proposal=…) to the product form once it is ready. Nothing is saved here: the merchant
 * reviews and presses Save, which goes through the normal product endpoints. Returns what the page needs to show the
 * review state and to report the outcome.
 */
export const useProductProposal = (form: UseFormReturn<ProductModel.ProductFormData>, opts: { productId?: string; ready: boolean }) => {
  const [params, setParams] = useSearchParams()
  const proposalUid = params.get('proposal')
  const { markProposal } = useAssistant()
  const [proposal, setProposal] = useState<ProductProposal | null>(null)
  const [changed, setChanged] = useState<Set<string>>(new Set())
  const applied = useRef<string | null>(null)
  const stamp = useReviewStamp()

  // Review clicked again for the proposal already on screen: just bring it into view.
  useEffect(() => {
    if (stamp && proposal?.proposal_uid === proposalUid) scrollReviewIntoView()
    // Only a new click should scroll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stamp])

  useEffect(() => {
    if (!proposalUid) {
      setProposal(null)
      setChanged(new Set())
      return
    }
    AssistantModel.getProposal(proposalUid).then((res) => {
      const p = res.success ? res.data?.result : null
      // Stock changes on this page are reviewed in its Inventory tab (useStockProposal).
      if (p?.kind === 'stock_update') return
      const fits = p && ((p.kind === 'product_create' && !opts.productId) || (p.kind === 'product_update' && p.target_id === opts.productId))
      if (!p || !fits || p.status !== 'pending') {
        if (p && p.status !== 'pending') toast.info('This change was already handled.')
        setParams({}, { replace: true })
        return
      }
      setProposal((current) => (current?.proposal_uid === p.proposal_uid ? current : (p as ProductProposal)))
    })
  }, [proposalUid, opts.productId, setParams])

  useEffect(() => {
    if (!proposal || !opts.ready || applied.current === proposal.proposal_uid) return
    // After the page's own reset of the loaded product.
    const timer = window.setTimeout(() => {
      applied.current = proposal.proposal_uid
      const fields = proposal.payload.fields as ProductProposalFields
      const names = new Set<string>(Object.keys(fields))
      ;(Object.entries(fields) as [keyof ProductModel.ProductFormData, unknown][]).forEach(([name, value]) => {
        form.setValue(name, value as never, { shouldDirty: true })
      })
      if (proposal.kind === 'product_create') {
        form.setValue('variations', proposal.payload.variations.map((v) => ({
          name: v.name,
          regular_price: v.regular_price,
          purchase_cost: v.purchase_cost ?? 0,
          initial_stock_quantity: v.initial_stock_quantity ?? 0,
          summary: '',
          description: '',
          delivery_return_policy: '',
          search_keywords: `${fields.name ?? ''}, ${v.name}`,
          attribute_value_ids: [],
          images: [],
        })), { shouldDirty: true })
        names.add('variations')
      } else if (proposal.payload.variation_prices.length) {
        const current = form.getValues('variations') ?? []
        form.setValue('variations', current.map((v) => {
          const change = proposal.payload.variation_prices.find((c) => c.variation_id === v._id)
          return change ? { ...v, ...(change.regular_price !== undefined && { regular_price: change.regular_price }), ...(change.purchase_cost !== undefined && { purchase_cost: change.purchase_cost }) } : v
        }), { shouldDirty: true })
        names.add('variations')
      }
      setChanged(names)
      scrollReviewIntoView()
    }, 0)
    return () => window.clearTimeout(timer)
  }, [proposal, opts.ready, form])

  // Saves made while this proposal is on screen are tagged with it in the audit log.
  useEffect(() => {
    if (!proposal) return
    setActiveProposal(proposal.proposal_uid)
    return () => setActiveProposal(null)
  }, [proposal])

  const finish = useCallback((status: 'published' | 'discarded') => {
    if (!proposal) return
    markProposal(proposal.proposal_uid, status)
    setProposal(null)
    setChanged(new Set())
  }, [markProposal, proposal])

  return {
    proposal,
    changed,
    onSaved: () => finish('published'),
    discard: () => {
      finish('discarded')
      // Back to what the form held before the assistant's values (the loaded product, or an empty new product).
      form.reset()
      setParams({}, { replace: true })
    },
  }
}
